// 007 / 005 slice (P-HOME-1; SA-BAN-6): the Home hero. Shows a school's live banners one at a time
// (1600 × 480, title as the image's accessible name) with Previous / Next and "1 of N"; with no banners,
// a plain hero with the school's name. Also the school admin's live preview.
import { useState } from "react";
import { Image, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

export type HeroBanner = { id: string; title: string; imageUrl: string };

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    hero: {
      borderRadius: t.radius.card,
      overflow: "hidden",
      backgroundColor: t.color.primarySoft,
    },
    image: { width: "100%", aspectRatio: 1600 / 480 },
    fallback: { padding: t.space.x2l, gap: t.space.sm },
    controls: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: t.space.sm,
      paddingHorizontal: t.space.md,
      paddingVertical: t.space.xs,
      backgroundColor: t.color.surface,
    },
    arrow: { paddingHorizontal: t.space.md, justifyContent: "center" },
  }),
);

export function HeroCarousel({
  banners,
  schoolName,
  fallbackText,
  testID = "home-hero",
}: {
  banners: readonly HeroBanner[];
  schoolName: string;
  fallbackText: string;
  testID?: string;
}) {
  const styles = useStyles();
  const [index, setIndex] = useState(0);
  if (banners.length === 0) {
    return (
      <View
        style={[styles.hero, styles.fallback]}
        testID={`${testID}-fallback`}
      >
        <Text variant="heading" size="x2l">
          {schoolName}
        </Text>
        <Text tone="muted">{fallbackText}</Text>
      </View>
    );
  }
  const at = Math.min(index, banners.length - 1);
  const banner = banners[at]!;
  const go = (step: number) =>
    setIndex((at + step + banners.length) % banners.length);
  return (
    <View
      style={styles.hero}
      testID={testID}
      role="region"
      aria-roledescription="carousel"
      aria-label={schoolName}
    >
      <Image
        source={{ uri: banner.imageUrl }}
        style={styles.image}
        accessibilityLabel={banner.title}
        accessible
        resizeMode="cover"
        testID={`${testID}-image`}
      />
      {banners.length > 1 ? (
        <View style={styles.controls}>
          <PressableTarget
            accessibilityLabel="Previous banner"
            onPress={() => go(-1)}
            style={styles.arrow}
          >
            <Text aria-hidden>‹</Text>
          </PressableTarget>
          <Text size="sm" testID={`${testID}-position`}>
            {`${at + 1} of ${banners.length}`}
          </Text>
          <PressableTarget
            accessibilityLabel="Next banner"
            onPress={() => go(1)}
            style={styles.arrow}
          >
            <Text aria-hidden>›</Text>
          </PressableTarget>
        </View>
      ) : null}
    </View>
  );
}
